import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Loader2 } from "@digital-twins/design-system/icons";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import type { CoverageLevel, QuestionCoverageResult } from "@digital-twins/shared-types";
import { AnalyticsEvents, track } from "@/lib/services/analytics";

export type CoverageEntry = QuestionCoverageResult | "computing";

interface TopicCoverageChipProps {
  coverageResult: CoverageEntry | undefined;
  questionId: string;
}

// The two findings about the audience, each with its label. Coverage is
// advisory and never blocks, so both share the amber warning tint; the label
// and the tooltip say which it is. Covered, and a question nothing could
// assess (no audience, too little audience data, a scoring outage), show
// nothing.
const CHIP_LABEL_KEYS: Partial<Record<CoverageLevel, "partialCoverage" | "unsupportedByAudience">> = {
  partial: "partialCoverage",
  out_of_domain: "unsupportedByAudience",
};

/**
 * Advisory indicator shown on a QuestionnaireQuestionCard header.
 *
 * States:
 * - computing  → bare spinner (transient, frontend-only)
 * - partial, out_of_domain → amber "!" badge + rationale tooltip
 * - covered, unavailable → render nothing
 */
export function TopicCoverageChip({ coverageResult, questionId }: TopicCoverageChipProps) {
  const { t } = useTranslation("app");
  const level =
    coverageResult === "computing"
      ? "computing"
      : coverageResult?.level ?? null;
  const labelKey =
    level !== null && level !== "computing" ? CHIP_LABEL_KEYS[level] : undefined;

  // Track chip views for the findings shown (after all hooks).
  useEffect(() => {
    if (labelKey) {
      track(AnalyticsEvents.COVERAGE_CHIP_VIEWED, {
        question_id: questionId,
        level,
      });
    }
  // Only fire when the level or question changes.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [level, questionId]);

  if (!coverageResult || level === null) return null;

  const testId = `questionnaire-topic-coverage-${questionId}`;

  if (level === "computing") {
    // Bare spinner, no pill and no label: this state is transient and advisory,
    // and a worded chip in the header competed with the question's own type and
    // exposure chips for the same glance. The wording survives as the
    // aria-label, so a screen reader still hears what is happening.
    return (
      <span
        role="status"
        className="inline-flex h-5 w-5 shrink-0 items-center justify-center text-muted-foreground/60"
        data-testid={testId}
        aria-label={t("questionnaire.assessingCoverage")}
      >
        <Loader2 className="h-3 w-3 animate-spin" />
      </span>
    );
  }

  if (!labelKey || coverageResult === "computing") return null;

  const label = t(`questionnaire.${labelKey}`);

  return (
    <TooltipProvider delayDuration={300}>
      <Tooltip>
        <TooltipTrigger asChild>
          <span
            className="inline-flex h-5 w-5 shrink-0 cursor-default items-center justify-center rounded-full bg-chart-warning/15 text-[12px] font-bold leading-none text-chart-warning"
            data-testid={testId}
            aria-label={label}
          >
            !
          </span>
        </TooltipTrigger>
        <TooltipContent side="right" className="max-w-[260px] text-[12px] leading-snug">
          {coverageResult.rationale || label}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
